import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { api, errorText } from "./api";
import { copyToClipboard } from "./clipboard";
import { playLocal } from "./audio";
import { AdminPanel } from "./components/AdminPanel";
import { CommandPalette, type Command } from "./components/CommandPalette";
import { Composer } from "./components/Composer";
import { ConnectionBanner } from "./components/ConnectionBanner";
import { Dialogs } from "./components/Dialogs";
import { Feed, Queue } from "./components/Feed";
import { FirstRun } from "./components/FirstRun";
import { Header } from "./components/Header";
import { Celebration } from "./components/Celebration";
import { Login } from "./components/Login";
import { MiniShell, openMiniWindow } from "./components/MiniShell";
import { MobileShell } from "./components/MobileShell";
import { isV3, snapStability } from "./components/ModelPicker";
import { OnAir } from "./components/NowPlaying";
import { SettingsDrawer } from "./components/SettingsDrawer";
import { PanelSkeleton } from "./components/Skeleton";
import { Soundboard } from "./components/Soundboard";
import { padName } from "./components/Feed";
import { CreditsCard, TodayCard } from "./components/StatsCards";
import { Toasts } from "./components/Toasts";
import { confirmDialog, promptDialog } from "./dialog";
import { useIsMobile, useSocket } from "./hooks";
import {
  applyDensity,
  applyTheme,
  DEFAULT_SETTINGS,
  loadDensity,
  loadPrefs,
  savePrefs,
} from "./prefs";
import { clearLegacyPresets, loadLegacyPresets } from "./presets";
import { initialState, reducer, type State } from "./store";
import { toast, toastOnce } from "./toast";
import type {
  Bootstrap,
  KeyStatus,
  Preset,
  Quota,
  Row,
  SocketEvent,
  Subscription,
  TenantIdentity,
  TenantSettings,
  VoiceSettings,
  Voice,
} from "./types";

type Phase = "loading" | "login" | "ready";

/** True when a keystroke belongs to whatever the user is typing in. */
function isTyping(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  const tag = element.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    element.isContentEditable
  );
}

export function App({ tenant }: { tenant: TenantIdentity }) {
  const [phase, setPhase] = useState<Phase>("loading");
  /* The instance settings document — what config.yaml used to be. Sent only
     to a tenant admin; null for everyone else. */
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  /* Whether an ElevenLabs key is stored and how old it is — never the key
     itself. Admin-only, and the first step of the setup checklist. */
  const [keyStatus, setKeyStatus] = useState<KeyStatus | null>(null);
  const [state, dispatch] = useReducer(reducer, initialState);

  /* What the composer will send. Kept out of the reducer: the server never
     dictates it, and it is the one slice of state that survives a reload. */
  /* Restored from the last visit: a message half-typed when the tab was closed
     or the laptop slept is not something to make somebody type again. */
  const [text, setText] = useState(() => loadPrefs().draft ?? "");
  const [previewFirst, setPreviewFirst] = useState(() => loadPrefs().preview_first ?? false);
  const [sentAt, setSentAt] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const [voiceId, setVoiceId] = useState("");
  const [modelId, setModelId] = useState("");
  const [settings, setSettings] = useState<VoiceSettings>(DEFAULT_SETTINGS);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [sending, setSending] = useState(false);
  const [mutesEpoch, setMutesEpoch] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [hideFirstRun, setHideFirstRun] = useState(() => loadPrefs().hide_first_run ?? false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  /* Which shared character the composer is speaking as, by its number. Not a
     highlight this time: it is sent with the message, and the server takes the
     voice, the model and the tuning from that row rather than from the body —
     which is what makes the lock a lock. Nudging a chip by hand drops back to
     free-form, and under `presetsOnly` there are no chips to nudge. */
  const [activePreset, setActivePreset] = useState<number | null>(null);
  const [drawerTab, setDrawerTab] = useState<"settings" | "presets">("settings");
  /* Which shell renders. Everything above and below this line is shared: the
     two differ only in where the pieces are put, which is what keeps a feature
     added here from needing a second implementation for phones. */
  const mobile = useIsMobile();
  /* The narrow panel for a popout or an OBS dock. A path rather than a query
     string, so the dock URL is short enough to type into OBS by hand. */
  const miniView = typeof location !== "undefined" && location.pathname.replace(/\/+$/, "") === "/mini";

  const applyQuota = useCallback((quota: Quota | null | undefined) => {
    if (!quota) return;
    dispatch({ type: "quota", quota });
    if (quota.cooldown_left > 0) {
      setCooldownUntil(Date.now() + quota.cooldown_left * 1000);
    }
    if (quota.muted) {
      toastOnce("muted", "You are muted.", "error");
    }
  }, []);

  /* ------------------------------------------------------------- boot */

  const boot = useCallback(async () => {
    const who = await api<{ authenticated: boolean }>("/api/me");
    if (!who.authenticated) {
      setPhase("login");
      return;
    }
    const data = await api<Bootstrap>("/api/bootstrap");
    dispatch({ type: "bootstrap", data });
    setTenantSettings(data.settings);
    setKeyStatus(data.elevenlabs_key);

    const prefs = loadPrefs();

    const wantedModel = prefs.model_id || data.defaults.model_id;
    const model =
      data.models.find((candidate) => candidate.model_id === wantedModel) ??
      data.models[0];
    const nextModelId = model?.model_id ?? "";
    setModelId(nextModelId);

    const merged = { ...DEFAULT_SETTINGS, ...(prefs.settings || {}) };
    setSettings(
      isV3(nextModelId)
        ? { ...merged, stability: snapStability(merged.stability) }
        : merged,
    );

    const wantedVoice = prefs.voice_id || data.defaults.voice_id;
    const voice =
      data.voices.find((candidate) => candidate.voice_id === wantedVoice) ??
      data.voices[0];
    setVoiceId(voice?.voice_id ?? "");

    /* Under the preset lock a non-admin has nothing to choose from but the
       characters, so choose the first one rather than opening on a composer
       that refuses to send until something is clicked. */
    if (data.presets_only && !data.me.admin) {
      setActivePreset(data.presets[0]?.id ?? null);
    }

    applyQuota(data.quota);
    for (const warning of data.warnings) toast(warning, "error");
    setPhase("ready");
  }, [applyQuota]);

  useEffect(() => {
    boot().catch((error) => {
      setPhase("login");
      toast(errorText(error), "error");
    });
  }, [boot]);

  /* Remember the composer's choices, but only once there is something real to
     remember - the empty state before bootstrap would wipe them. */
  useEffect(() => {
    if (phase !== "ready") return;
    savePrefs({ voice_id: voiceId, model_id: modelId, settings });
  }, [phase, voiceId, modelId, settings]);

  /* The draft, saved a moment after typing stops rather than on every key. */
  useEffect(() => {
    const timer = window.setTimeout(() => savePrefs({ draft: text }), 300);
    return () => window.clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    savePrefs({ preview_first: previewFirst });
  }, [previewFirst]);

  /* The instance's accent, on the root so every token that derives from it
     follows. The server only ever sends a name from the fixed palette. */
  useEffect(() => {
    if (state.accent === "blue") delete document.documentElement.dataset.accent;
    else document.documentElement.dataset.accent = state.accent;
  }, [state.accent]);

  /* The first time this browser sees an overlay come online, say so with a
     little more than a toast: it is the moment a new instance starts working.
     Only a transition seen while the panel is open counts — an overlay that
     was already connected at boot has nothing to celebrate. */
  const lastOverlays = useRef<number | null>(null);
  useEffect(() => {
    if (phase !== "ready") return;
    const before = lastOverlays.current;
    lastOverlays.current = state.overlays;
    if (before === 0 && state.overlays > 0 && !loadPrefs().celebrated_overlay) {
      savePrefs({ celebrated_overlay: true });
      setCelebrate(true);
      toast("Overlay connected", "ok");
    }
  }, [phase, state.overlays]);

  /* --------------------------------------------------------- websocket */

  const reloadPresets = useCallback(async () => {
    try {
      dispatch({ type: "presets", presets: await api<Preset[]>("/api/presets") });
    } catch (error) {
      toast(errorText(error), "error");
    }
  }, []);

  const onEvent = useCallback(
    (frame: SocketEvent) => {
      if (frame.event === "toast") toast(frame.data.text);
      // The list itself is not on the wire: the hub broadcasts to the instance,
      // not to a person, so each panel asks for its own filtered copy.
      if (frame.event === "presets.changed") void reloadPresets();
      dispatch({ type: "socket", frame });
    },
    [reloadPresets],
  );

  const socketStatus = useSocket(phase === "ready", onEvent);

  /* ------------------------------------------------------------ actions */

  const selectedVoice =
    state.voices.find((voice) => voice.voice_id === voiceId) ?? null;
  const selectedModel =
    state.models.find((model) => model.model_id === modelId) ?? null;

  async function send() {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const result = await api<{ id: number; quota: Quota }>("/api/speak", {
        method: "POST",
        // With a character chosen, the number is the whole request: the server
        // reads the voice, the model and the tuning off that row and ignores
        // anything the body claims about them.
        body: JSON.stringify({
          ...(activePreset !== null
            ? { text: body, preset: activePreset }
            : { text: body, voice_id: voiceId, model_id: modelId, settings }),
          preview: previewFirst,
        }),
      });
      setText("");
      savePrefs({ draft: "" });
      setSentAt(Date.now());
      applyQuota(result.quota);
      toast(
        previewFirst ? `Generating a preview of #${result.id}` : `Queued #${result.id}`,
        "ok",
      );
    } catch (error) {
      toast(errorText(error), "error");
    } finally {
      setSending(false);
    }
  }

  function changeModel(next: string) {
    setModelId(next);
    setActivePreset(null);
    // v3 only understands Creative / Natural / Robust; snap on the way in so
    // the slider never shows a value the server would quietly move.
    if (isV3(next)) {
      setSettings((current) => ({
        ...current,
        stability: snapStability(current.stability),
      }));
    }
  }

  /* After a refetch the voice objects are new, so re-point the selection at
     the fresh one (or fall back to the first voice if it vanished upstream). */
  function adoptVoices(voices: Voice[]) {
    dispatch({ type: "voices", voices });
    if (!voices.some((voice) => voice.voice_id === voiceId)) {
      setVoiceId(voices[0]?.voice_id ?? "");
    }
  }

  const focusComposer = useCallback(() => {
    const field = textRef.current;
    if (!field) return;
    field.focus();
    field.scrollIntoView({ block: "nearest" });
  }, []);

  function reuse(row: Row) {
    setText(row.text);
    // Said as a character that still exists? Then reusing it means that
    // character again — including which browser source it comes out of.
    const spokenAs =
      row.preset !== null
        ? (state.presets.find((preset) => preset.id === row.preset) ?? null)
        : null;
    setActivePreset(spokenAs?.id ?? null);
    if (state.voices.some((voice) => voice.voice_id === row.voice_id)) {
      setVoiceId(row.voice_id);
    }
    if (state.models.some((model) => model.model_id === row.model_id)) {
      changeModel(row.model_id);
    }
    setSettings({ ...DEFAULT_SETTINGS, ...row.settings });
    focusComposer();
  }

  /* ----------------------------------------------------------- presets */

  const applyPreset = useCallback((preset: Preset) => {
    // The chips follow the character even though the server no longer reads
    // them: seeing which voice you are about to speak as is the point of them.
    setVoiceId(preset.voice_id);
    setModelId(preset.model_id);
    setSettings({
      ...DEFAULT_SETTINGS,
      ...preset.settings,
      ...(isV3(preset.model_id)
        ? { stability: snapStability(preset.settings.stability) }
        : {}),
    });
    setActivePreset(preset.id);
  }, []);

  /* Publish what is in the composer as a character everyone can use. Admin-only,
     because a preset is instance configuration now rather than one browser's
     shortcut — and because it may be about to get an OBS source of its own. */
  async function savePreset() {
    if (!selectedVoice) {
      toast("Pick a voice first", "error");
      return;
    }
    const name = await promptDialog({
      title: "Save as a character",
      body: `${selectedVoice.name}${selectedModel ? ` · ${selectedModel.name}` : ""} with the current settings.`,
      confirmLabel: "Save",
      prompt: {
        label: "Call it",
        placeholder: "Grandma",
        initial: selectedVoice.name,
        maxLength: 24,
      },
    });
    if (!name) return;

    try {
      const preset = await api<Preset>("/api/admin/presets", {
        method: "POST",
        body: JSON.stringify({
          name,
          voice_id: voiceId,
          model_id: modelId,
          settings,
        }),
      });
      await reloadPresets();
      setActivePreset(preset.id);
      toast(`Saved as ${name}`, "ok");
    } catch (error) {
      toast(errorText(error), "error");
    }
  }

  /* A character can be deleted, renamed or locked by an admin while somebody
     else has it selected. Dropping the selection is the honest response: the
     composer then says what it needs, rather than sending a number the server
     will refuse. */
  useEffect(() => {
    if (activePreset === null) return;
    if (!state.presets.some((preset) => preset.id === activePreset)) {
      setActivePreset(null);
    }
  }, [state.presets, activePreset]);

  /* One-time offer: the presets this browser stored before they became instance
     configuration. Only an admin can create the shared rows, and only they are
     asked — a mod's old shortcuts are not theirs to publish to everybody. */
  const offeredImport = useRef(false);
  useEffect(() => {
    if (phase !== "ready" || !state.me?.admin || offeredImport.current) return;
    const legacy = loadLegacyPresets();
    if (legacy.length === 0) return;
    offeredImport.current = true;

    void (async () => {
      const yes = await confirmDialog({
        title: `Import ${legacy.length} preset${legacy.length === 1 ? "" : "s"}?`,
        body: "This browser has saved presets. Import them as shared characters?",
        confirmLabel: "Import",
      });
      // Either way they are done with: asked and declined is an answer, and
      // asking again on every reload is not.
      clearLegacyPresets();
      if (!yes) return;
      for (const preset of legacy) {
        try {
          await api("/api/admin/presets", {
            method: "POST",
            body: JSON.stringify({
              name: preset.name,
              voice_id: preset.voice_id,
              model_id: preset.model_id,
              settings: preset.settings,
            }),
          });
        } catch (error) {
          // One that names a voice this account lost should not stop the rest.
          toast(`Could not import “${preset.name}”: ${errorText(error)}`, "error");
        }
      }
      await reloadPresets();
    })();
  }, [phase, state.me, reloadPresets]);

  function dismissFirstRun() {
    setHideFirstRun(true);
    savePrefs({ hide_first_run: true });
  }

  async function signOut() {
    await api("/api/logout", { method: "POST" }).catch(() => {});
    location.reload();
  }

  const me = state.me;
  const admin = me?.admin ?? false;

  /* What to call a browser source on screen. A stage is a preset's number, so
     the name is that character's — and a number whose preset has since been
     deleted is still worth naming rather than dropping. */
  const stageName = useCallback(
    (stage: number | null): string => {
      if (stage === null) return "the main overlay";
      const preset = state.presets.find((candidate) => candidate.id === stage);
      return preset ? preset.name : `character #${stage}`;
    },
    [state.presets],
  );

  const setLocked = useCallback(
    async (locked: boolean) => {
      try {
        await api("/api/admin/lock", {
          method: "POST",
          body: JSON.stringify({ locked }),
        });
      } catch (error) {
        toast(errorText(error), "error");
      }
    },
    [],
  );

  const refreshUpstream = useCallback(async () => {
    try {
      adoptVoices(await api<Voice[]>("/api/voices?refresh=true"));
      dispatch({
        type: "subscription",
        subscription: await api<Subscription>("/api/subscription"),
      });
      toast("Refreshed", "ok");
    } catch (error) {
      toast(errorText(error), "error");
    }
    // adoptVoices closes over voiceId, and re-pointing the selection is the
    // whole job — so this deliberately re-reads it rather than being memoised
    // against a stale one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceId]);

  /* -------------------------------------------------------- the commands */

  const commands = useMemo<Command[]>(() => {
    const list: Command[] = [
      {
        id: "compose",
        label: "Write a message",
        hint: "/",
        run: focusComposer,
      },
      ...state.presets.map((preset, index) => ({
        id: `preset-${preset.id}`,
        label: `Speak as ${preset.name}`,
        hint: `${index + 1}${preset.locked ? " · admins only" : ""}`,
        run: () => applyPreset(preset),
      })),
      {
        id: "last-clip",
        label: "Play the last clip here",
        hint: "local",
        run: () =>
          state.lastAudioUrl
            ? playLocal(state.lastAudioUrl)
            : toast("Nothing generated yet"),
      },
      {
        id: "refresh",
        label: "Refresh voices and credits",
        run: () => void refreshUpstream(),
      },
      {
        id: "search",
        label: "Search the history",
        run: () => {
          const field = document.getElementById("history-search") as HTMLInputElement | null;
          field?.focus();
          field?.scrollIntoView({ block: "center" });
        },
      },
      {
        id: "preview-first",
        label: previewFirst ? "Send straight to stream" : "Preview before sending",
        run: () => setPreviewFirst(!previewFirst),
      },
      {
        id: "mini",
        label: "Open the mini panel",
        hint: "popout or OBS dock",
        run: openMiniWindow,
      },
      {
        id: "density",
        label: loadDensity() === "compact" ? "Comfortable spacing" : "Compact spacing",
        run: () => applyDensity(loadDensity() === "compact" ? "comfortable" : "compact"),
      },
      // Pads are on the palette because the palette is where a keyboard goes
      // mid-stream: ⌘K, two letters of the pad's name, Enter.
      ...state.soundboard.map((row) => ({
        id: `pad-${row.id}`,
        label: `Play: ${padName(row)}`,
        hint: "soundboard · free",
        run: () =>
          void api(`/api/requests/${row.id}/replay`, { method: "POST" }).catch((error) =>
            toast(errorText(error), "error"),
          ),
      })),
    ];

    if (admin) {
      list.push(
        {
          id: "lock",
          label: state.locked ? "Unlock TTS" : "Lock TTS",
          danger: !state.locked,
          run: () => void setLocked(!state.locked),
        },
        {
          id: "overlay-test",
          label: "Play test tone",
          hint: "free",
          run: () =>
            void api("/api/admin/overlay/test", { method: "POST" })
              .then(() => toast("Test tone sent"))
              .catch((error) => toast(errorText(error), "error")),
        },
        {
          id: "settings",
          label: "Open instance settings",
          run: () => {
            setDrawerTab("settings");
            setSettingsOpen(true);
          },
        },
        {
          id: "characters",
          label: "Manage characters",
          run: () => {
            setDrawerTab("presets");
            setSettingsOpen(true);
          },
        },
      );
      /* The OBS URLs, on a keystroke.

         Copying one is the most repeated admin action there is — every scene
         rebuild, every time OBS forgets a source, every time somebody else is
         running the stream — and the alternative is four clicks into a drawer.
         One entry per source, named after the character, so Ctrl-K + the first
         letters of its name is the whole interaction. */
      if (state.overlayUrl) {
        list.push({
          id: "copy-overlay-url",
          label: "Copy the main OBS source URL",
          hint: "browser source",
          run: () => void copyToClipboard(state.overlayUrl ?? "", "Main overlay URL copied"),
        });
      }
      for (const preset of state.presets) {
        if (!preset.overlay.own_source || !preset.overlay_url) continue;
        list.push({
          id: `copy-overlay-${preset.id}`,
          label: `Copy ${preset.name}'s OBS source URL`,
          hint: "browser source",
          run: () =>
            void copyToClipboard(preset.overlay_url ?? "", `${preset.name} URL copied`),
        });
      }
      for (const playing of state.nowPlaying) {
        list.push({
          id: `skip-${playing.row.id}`,
          label: `Skip #${playing.row.id} on stream`,
          hint: playing.stage === null ? undefined : `on ${stageName(playing.stage)}`,
          danger: true,
          run: () =>
            void api(`/api/requests/${playing.row.id}/skip`, { method: "POST" }).catch(
              (error) => toast(errorText(error), "error"),
            ),
        });
      }
    }

    for (const theme of ["light", "dark", "system"] as const) {
      list.push({
        id: `theme-${theme}`,
        label: `Theme: ${theme}`,
        run: () => applyTheme(theme),
      });
    }
    list.push({ id: "sign-out", label: "Sign out", run: () => void signOut() });
    return list;
  }, [
    admin,
    applyPreset,
    focusComposer,
    previewFirst,
    refreshUpstream,
    setLocked,
    stageName,
    state.soundboard,
    state.lastAudioUrl,
    state.locked,
    state.nowPlaying,
    state.overlayUrl,
    state.presets,
  ]);

  /* ------------------------------------------------------------ keyboard */

  useEffect(() => {
    if (phase !== "ready") return;

    function onKeyDown(event: KeyboardEvent) {
      // ⌘K is the one shortcut that has to work from inside a field, because
      // half of what it reaches is what you would otherwise stop typing to go
      // and find.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((was) => !was);
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;

      if (event.key === "/") {
        event.preventDefault();
        focusComposer();
        return;
      }
      if (event.key >= "1" && event.key <= "9") {
        const preset = state.presets[Number(event.key) - 1];
        if (!preset) return;
        event.preventDefault();
        applyPreset(preset);
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [phase, state.presets, applyPreset, focusComposer]);

  /* -------------------------------------------------------------- render */

  if (phase === "loading") {
    return (
      <>
        <PanelSkeleton />
        <Toasts />
      </>
    );
  }

  if (phase === "login") {
    return (
      <>
        <Login tenant={tenant} onSignedIn={() => void boot()} />
        <Toasts />
      </>
    );
  }

  if (!me) return <Toasts />;

  const openSettings = () => {
    setDrawerTab("settings");
    setSettingsOpen(true);
  };
  const openPresets = () => {
    setDrawerTab("presets");
    setSettingsOpen(true);
  };

  /* The panel's pieces, built once.

     Both shells are handed these already constructed, so neither can decide a
     phone gets a smaller Composer or an admin card with a tab missing. The two
     layouts differ in arrangement only — which is the property that stops the
     mobile view quietly becoming a subset the next time something is added. */

  const firstRunSlot =
    admin && !hideFirstRun ? (
      <FirstRun
        keyStatus={keyStatus}
        overlaysConnected={state.overlays}
        hasSpoken={state.history.length > 0}
        overlayUrl={state.overlayUrl}
        onOpenSettings={openSettings}
        onTestLine={() => {
          setText("Hello! This is a test of the text to speech.");
          focusComposer();
        }}
        onDismiss={dismissFirstRun}
      />
    ) : null;

  /* This sender's previews. An admin sees only their own here too: someone
     else's preview is theirs to decide about, and it is in the history. */
  const myHeld = state.history.filter((row) => row.status === "held" && row.user === me.name);

  const composerSlot = (
    <Composer
      text={text}
      setText={setText}
      textRef={textRef}
      limits={state.limits}
      quota={state.quota}
      locked={state.locked}
      admin={admin}
      cooldownUntil={cooldownUntil}
      lastAudioUrl={state.lastAudioUrl}
      sending={sending}
      onSend={() => void send()}
      voices={state.voices}
      models={state.models}
      selectedVoice={selectedVoice}
      selectedModel={selectedModel}
      settings={settings}
      modelId={modelId}
      onVoice={(voice) => {
        setVoiceId(voice.voice_id);
        setActivePreset(null);
      }}
      onModel={changeModel}
      onSettings={(next) => {
        setSettings(next);
        setActivePreset(null);
      }}
      onRefreshVoices={() => void refreshUpstream()}
      presets={state.presets}
      activePreset={activePreset}
      presetsOnly={state.presetsOnly}
      onApplyPreset={applyPreset}
      onClearPreset={() => setActivePreset(null)}
      onSavePreset={() => void savePreset()}
      onManagePresets={openPresets}
      previewFirst={previewFirst}
      onPreviewFirst={setPreviewFirst}
      held={myHeld}
      sentAt={sentAt}
      compact={miniView}
    />
  );

  const soundboardSlot = (
    <Soundboard pads={state.soundboard} me={me} presets={state.presets} compact={miniView} />
  );

  const onAirSlot = (
    <OnAir
      playing={state.nowPlaying}
      history={state.history}
      me={me}
      stageName={stageName}
      onReuse={reuse}
      onMuted={() => setMutesEpoch((epoch) => epoch + 1)}
    />
  );

  const queueSlot = (
    <Queue
      history={state.history}
      me={me}
      nowPlayingIds={state.nowPlaying.map((playing) => playing.row.id)}
      onReuse={reuse}
      onMuted={() => setMutesEpoch((epoch) => epoch + 1)}
    />
  );

  const feedSlot = (
    <Feed
      history={state.history}
      me={me}
      presets={state.presets}
      onReuse={reuse}
      onMuted={() => setMutesEpoch((epoch) => epoch + 1)}
    />
  );

  const statsSlot = (
    <>
      <TodayCard stats={state.stats} />
      <CreditsCard subscription={state.subscription} stats={state.stats} />
    </>
  );

  const adminSlot = admin ? (
    <AdminPanel
      volume={state.volume}
      tuber={state.tuber}
      overlayUrl={state.overlayUrl}
      stages={state.stages}
      presets={state.presets}
      mutesEpoch={mutesEpoch}
      onManagePresets={openPresets}
      onVoices={adoptVoices}
      onSubscription={(subscription) => dispatch({ type: "subscription", subscription })}
      onTuber={(tuber) => dispatch({ type: "tuber", tuber })}
    />
  ) : null;

  const bannerSlot = <ConnectionBanner status={socketStatus} />;

  const announcer = <NowPlayingAnnouncer playing={state.nowPlaying} />;

  if (miniView) {
    return (
      <>
        <MiniShell
          state={state}
          socketStatus={socketStatus}
          onLock={setLocked}
          banner={bannerSlot}
          composer={composerSlot}
          onAir={onAirSlot}
          soundboard={soundboardSlot}
        />
        {announcer}
        <CommandPalette open={paletteOpen} commands={commands} onClose={() => setPaletteOpen(false)} />
        <Dialogs />
        <Toasts />
      </>
    );
  }

  return (
    <>
      {announcer}
      {celebrate && <Celebration onDone={() => setCelebrate(false)} />}
      {mobile ? (
        <MobileShell
          state={state}
          socketStatus={socketStatus}
          stageName={stageName}
          onLock={setLocked}
          onSignOut={signOut}
          onOpenSettings={openSettings}
          onOpenPresets={openPresets}
          onRefresh={() => void refreshUpstream()}
          banner={bannerSlot}
          firstRun={firstRunSlot}
          composer={composerSlot}
          soundboard={soundboardSlot}
          queue={queueSlot}
          feed={feedSlot}
          stats={statsSlot}
          admin={adminSlot}
        />
      ) : (
        <div id="app">
          <Header
            state={state}
            socketStatus={socketStatus}
            onLock={setLocked}
            onSignOut={signOut}
            onOpenPalette={() => setPaletteOpen(true)}
            onOpenSettings={openSettings}
          />

          {bannerSlot}

          <main className="two-up">
            <section className="middle">
              {firstRunSlot}
              {composerSlot}

              {/* What is sounding right now and what is next: one card of
                  fixed height, always present, so a clip starting or a message
                  arriving never moves the history underneath it. */}
              {onAirSlot}

              {feedSlot}
            </section>

            <aside className="right">
              {soundboardSlot}
              {statsSlot}
              {adminSlot}
            </aside>
          </main>
        </div>
      )}

      {admin && (
        <SettingsDrawer
          open={settingsOpen}
          tab={drawerTab}
          onClose={() => setSettingsOpen(false)}
          settings={tenantSettings}
          models={state.models}
          voices={state.voices}
          presets={state.presets}
          stages={state.stages}
          onPresetsChanged={() => void reloadPresets()}
          keyStatus={keyStatus}
          onSaved={(next) => {
            setTenantSettings(next);
            dispatch({ type: "limits", limits: next.limits });
          }}
          onKeySaved={setKeyStatus}
        />
      )}

      <CommandPalette
        open={paletteOpen}
        commands={commands}
        onClose={() => setPaletteOpen(false)}
      />
      <Dialogs />
      <Toasts />
    </>
  );
}

/* Says what started playing, for a screen reader. The on-air card is visual
   and updates in place; without this, somebody listening to the panel rather
   than looking at it would only learn a clip had started from the stream. */
function NowPlayingAnnouncer({ playing }: { playing: State["nowPlaying"] }) {
  const [message, setMessage] = useState("");
  const seen = useRef<Set<number>>(new Set());
  useEffect(() => {
    const fresh = playing.filter((entry) => !seen.current.has(entry.row.id));
    seen.current = new Set(playing.map((entry) => entry.row.id));
    if (fresh.length) {
      setMessage(
        fresh.map((entry) => `Now playing, from ${entry.row.user}: ${entry.row.text}`).join(". "),
      );
    }
  }, [playing]);
  return (
    <div className="sr-only" role="status" aria-live="polite">
      {message}
    </div>
  );
}
